/**
 * Order Detail Drawer
 * Tabbed interface with Overview and Shipments tabs
 * Uses BaseDrawer for consistent sizing
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation, normalizeEntity, useIsRTL, useDispatch, useNavigate, useSelector } from '@spcc/core'
import {
  Badge,
  Button,
  cn,
  BaseDrawer,
} from '@spcc/uikit'
import {
  MapPin,
  Truck,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  CalendarDays,
  FileText,
  Gauge,
  Copy,
  ArrowUpRight,
  ShoppingCart,
  Loader2,
  Layers,
  Activity,
  MessageSquare,
  User,
  Phone,
  Pencil,
  Receipt,
  RefreshCw,
} from '@spcc/uikit'
import type { Order, OrderStatus } from '../../domain/types'
import { ordersActions } from '../../store/orders.slice'
import { shipmentsApi, ShipmentCard, type Shipment } from '@spcc/domains/shipments'
import { ordersApi } from '../../infrastructure/api/orders-api'
import { invoiceApi } from '@spcc/domains/finance'
import { SubCustomerSelector } from '@spcc/domains/sub-customers'
import type { SubCustomer } from '@spcc/domains/sub-customers'
import { OrderTimelineTab } from '../components/order-timeline/order-timeline-tab'
import { OrderSapSyncTab } from '../components/order-sap-sync-tab'
import {
  getLocalizedDeliveryLocation,
  getLocalizedProductName,
  getLocalizedQuantityUnit,
} from '../components/orders-section/utils'
import {
  shouldShowDeliveryNoteAction,
  shouldShowLegacyDrawerActions,
} from './order-detail-drawer.utils'
import { selectIsSuperAdmin, selectUserRoleType } from '@spcc/domains/auth'
import { ROLE_TYPES } from '@spcc/domains/auth'

interface OrderDetailDrawerProps {
  order: Order | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onViewSalesOrder?: (salesOrderId: string) => void
  onViewShipment?: (shipmentId: string) => void
  onCancel?: (order: Order) => void
  onOrderRefreshed?: (order: Order) => void
  onDownloadPdf?: (order: Order) => void
  onDownloadDeliveryNote?: (order: Order) => Promise<void> | void
  /** When true (e.g. native app), primary action opens in-app preview instead of a raw download */
  preferDeliveryNotePreview?: boolean
}

const statusConfig: Record<OrderStatus, {
  bg: string
  text: string
  border: string
  icon: typeof Clock
  gradient: string
}> = {
  pending: {
    bg: 'bg-slate-500/10',
    text: 'text-slate-600',
    border: 'border-slate-500/20',
    icon: Clock,
    gradient: 'from-slate-500 to-slate-600',
  },
  processing: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600',
    border: 'border-amber-500/20',
    icon: Package,
    gradient: 'from-amber-500 to-orange-500',
  },
  delivered: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600',
    border: 'border-emerald-500/20',
    icon: CheckCircle,
    gradient: 'from-emerald-500 to-emerald-600',
  },
  cancelled: {
    bg: 'bg-red-500/10',
    text: 'text-red-600',
    border: 'border-red-500/20',
    icon: XCircle,
    gradient: 'from-red-500 to-red-600',
  },
}

const statusSteps: OrderStatus[] = ['pending', 'processing', 'delivered']

type WeighbridgeUnit = 'kg' | 'tons'

const toKg = (value: number, _unit: WeighbridgeUnit): number => value

const fromKg = (value: number, _unit: WeighbridgeUnit): number => value

export function OrderDetailDrawer({
  order,
  open,
  onOpenChange,
  onViewSalesOrder,
  onViewShipment,
  onCancel,
  onOrderRefreshed,
  onDownloadPdf,
  onDownloadDeliveryNote,
  preferDeliveryNotePreview = false,
}: OrderDetailDrawerProps) {
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  const userRoleType = useSelector(selectUserRoleType)
  const isPackhouseManager = userRoleType === ROLE_TYPES.PACKHOUSE_MANAGER
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [overviewOrder, setOverviewOrder] = useState<Order | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [weighbridgeTickets, setWeighbridgeTickets] = useState<any[]>([])
  const [shipmentsLoading, setShipmentsLoading] = useState(false)
  const [weighbridgeLoading, setWeighbridgeLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [smsLoading, setSmsLoading] = useState(false)
  const [smsStatus, setSmsStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [isEditingSubCustomer, setIsEditingSubCustomer] = useState(false)
  const [subCustomerSaving, setSubCustomerSaving] = useState(false)
  const [orderInvoice, setOrderInvoice] = useState<{ invoiceNumber: string; total: number; currency: string } | null>(null)

  // When a shipment's resource is updated, sync the driver/vehicle info back to:
  // 1. The local display order (immediate optimistic update)
  // 2. The orders Redux store (so the orders table row updates immediately)
  // Then re-fetch the full order from the API to get server-enriched data
  const handleShipmentResourceUpdated = useCallback(
    async (driverName: string | undefined, vehicleLicensePlate: string | undefined) => {
      if (!order?.id) return

      // Optimistic update: use values from the shipment response right away
      setOverviewOrder(prev =>
        prev ? { ...prev, driverName, vehicleLicensePlate } : prev
      )
      dispatch(ordersActions.updateOrderDriverInfo({ orderId: order.id, driverName, vehicleLicensePlate }))

      // Definitive update: re-fetch the order so the orders table gets
      // the server-enriched driverName/vehicleLicensePlate immediately
      try {
        const freshOrder = await ordersApi.getOrder(order.id)
        if (freshOrder) {
          setOverviewOrder(freshOrder)
          dispatch(ordersActions.updateOrderInList(freshOrder))
          // Notify the parent section so it can update its local selectedOrder state
          // and force the orders table to re-render with the latest data
          onOrderRefreshed?.(freshOrder)
        }
      } catch {
        // Optimistic update already applied above — ignore re-fetch errors
      }
    },
    [order?.id, dispatch, onOrderRefreshed]
  )

  const handleSubCustomerSelected = useCallback(
    async (subCustomer: SubCustomer | null) => {
      if (!order?.id) return
      setSubCustomerSaving(true)
      try {
        // Optimistic update
        setOverviewOrder((prev) =>
          prev
            ? {
                ...prev,
                subCustomerId: subCustomer?.id ?? null,
                subCustomerDocumentId: subCustomer?.documentId ?? null,
                subCustomerName: subCustomer?.name ?? null,
                subCustomerPhone: subCustomer?.phone ?? null,
                subCustomerCode: subCustomer?.code ?? null,
              }
            : prev,
        )
        // Persist
        await ordersApi.updateOrder(order.id, {
          subCustomer: subCustomer?.documentId ?? null,
        })
        setIsEditingSubCustomer(false)
        // Definitive re-fetch
        const freshOrder = await ordersApi.getOrder(order.id)
        if (freshOrder) {
          setOverviewOrder(freshOrder)
          dispatch(ordersActions.updateOrderInList(freshOrder))
          onOrderRefreshed?.(freshOrder)
        }
      } catch {
        // Optimistic update remains; silently ignore network error
      } finally {
        setSubCustomerSaving(false)
      }
    },
    [order?.id, dispatch, onOrderRefreshed],
  )

  useEffect(() => {
    setOverviewOrder(order)
    setOverviewLoading(false)
  }, [order?.id])

  useEffect(() => {
    if (activeTab !== 'overview') return
    if (!order?.id) return

    let cancelled = false
    setOverviewLoading(true)

    ordersApi
      .getOrder(order.id)
      .then((freshOrder) => {
        if (cancelled) return
        setOverviewOrder(freshOrder ?? order)
      })
      .catch(() => {
        if (cancelled) return
        setOverviewOrder(order)
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false)
      })

    return () => { cancelled = true }
  }, [activeTab, order?.id])

  // Fetch invoice for this order
  useEffect(() => {
    if (!order?.orderNumber) {
      setOrderInvoice(null)
      return
    }
    invoiceApi.getInvoicesByOrderNumbers([order.orderNumber])
      .then((invoices) => {
        if (invoices.length > 0) {
          const inv = invoices[0]
          setOrderInvoice({ invoiceNumber: inv.invoiceNumber, total: inv.total.amount, currency: inv.total.currency })
        } else {
          setOrderInvoice(null)
        }
      })
      .catch(() => setOrderInvoice(null))
  }, [order?.orderNumber])

  useEffect(() => {
    if (!order) return

    const orderId = order.id
    let cancelled = false

    if (activeTab === 'shipments') {
      setShipmentsLoading(true)

      shipmentsApi
        .getShipmentsByOrderId(orderId)
        .then((data) => {
          if (cancelled) return
          setShipments(data)
        })
        .catch(() => {
          if (cancelled) return
          setShipments([])
        })
        .finally(() => {
          if (!cancelled) setShipmentsLoading(false)
        })
    }

    if (activeTab === 'weighbridge') {
      setWeighbridgeLoading(true)

      shipmentsApi
        .getOrderWeighbridgeTickets(orderId)
        .then((tickets) => {
          if (cancelled) return
          setWeighbridgeTickets(tickets)
        })
        .catch(() => {
          if (cancelled) return
          setWeighbridgeTickets([])
        })
        .finally(() => {
          if (!cancelled) setWeighbridgeLoading(false)
        })
    }

    return () => { cancelled = true }
  }, [activeTab, order?.id])

  useEffect(() => {
    if (!open) {
      setActiveTab('overview')
      setOverviewOrder(null)
      setOverviewLoading(false)
      setShipments([])
      setWeighbridgeTickets([])
      setShipmentsLoading(false)
      setWeighbridgeLoading(false)
      setSmsStatus('idle')
    }
  }, [open])

  if (!order) return null

  const displayOrder = overviewOrder ?? order

  const config = statusConfig[displayOrder.status] || statusConfig.pending
  const StatusIcon = config.icon
  const currentStepIndex = statusSteps.indexOf(displayOrder.status)
  const product = displayOrder.items[0]
  const productLabel = getLocalizedProductName(product?.product, i18n.language)
  const quantityUnit = getLocalizedQuantityUnit(
    product?.quantity.unit,
    i18n.t,
    i18n.language,
  )
  const deliveryLocation =
    getLocalizedDeliveryLocation(
      displayOrder.deliveryAddress,
      displayOrder.dropoffCity,
      i18n.language,
    ) ||
    t('common.notAvailable')
  const showLegacyDrawerActions = shouldShowLegacyDrawerActions(displayOrder.status)
  const showDeliveryNoteDownload = shouldShowDeliveryNoteAction(displayOrder.status, Boolean(onDownloadDeliveryNote))
  const sapOrderNumber = displayOrder.sapSalesOrderNumber?.trim()
  const canCancelOrder = displayOrder.status === 'pending' && !sapOrderNumber && !isPackhouseManager
  const isOrderEditable = displayOrder.status === 'pending'
  const showLoading =
    activeTab === 'overview'
      ? overviewLoading
      : activeTab === 'shipments'
        ? shipmentsLoading
        : activeTab === 'weighbridge'
          ? weighbridgeLoading
          : false

  // Aggregate weighbridge data
  const { summary } = (() => {
    const sum = {
      grossKg: 0,
      tareKg: 0,
      netKg: 0,
      hasGross: false,
      hasTare: false,
      hasNet: false,
    }

    const addWeight = (value: number | undefined, type: 'gross' | 'tare' | 'net') => {
      if (typeof value !== 'number' || !Number.isFinite(value)) return
      const valueKg = value

      if (type === 'gross') {
        sum.grossKg += valueKg
        sum.hasGross = true
      } else if (type === 'tare') {
        sum.tareKg += valueKg
        sum.hasTare = true
      } else {
        sum.netKg += valueKg
        sum.hasNet = true
      }
    }

    // Process explicit tickets ONLY
    weighbridgeTickets.forEach(rawTicket => {
      const ticket = normalizeEntity(rawTicket)
      if (!ticket) return

      const gross = ticket.grossWeight as number
      const tare = ticket.tareWeight as number
      const net = ticket.netWeight as number

      if (gross != null) addWeight(gross, 'gross')
      if (tare != null) addWeight(tare, 'tare')
      if (net != null) addWeight(net, 'net')
      else if (gross != null && tare != null) addWeight(gross - tare, 'net')
    })

    return { summary: sum }
  })()

  const weighbridgeUnit: WeighbridgeUnit = 'kg'
  const weighbridgeUnitLabel = getLocalizedQuantityUnit(
    weighbridgeUnit,
    i18n.t,
    i18n.language,
  )

  const formatWeightValue = (valueKg: number, hasValue: boolean) => {
    if (!hasValue) return t('common.notAvailable')
    const displayValue = fromKg(valueKg, weighbridgeUnit)
    const formatted = displayValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
    return `${formatted} ${weighbridgeUnitLabel}`
  }

  const handleViewSalesOrderClick = () => {
    const salesOrderId = displayOrder.contractId
    if (!salesOrderId) return

    const salesOrderNumber = displayOrder.salesOrderNumber?.trim()
    const searchValue = salesOrderNumber && salesOrderNumber !== '' ? salesOrderNumber : salesOrderId

    if (onViewSalesOrder) {
      onViewSalesOrder(salesOrderId)
      return
    }

    const basePath = window.location.pathname.startsWith('/orders') ? '/orders/sales-orders' : '/sales-orders'
    navigate({
      to: basePath as any,
      search: () => ({ search: searchValue }),
    })
  }

  const copyOrderId = () => {
    navigator.clipboard.writeText(displayOrder.orderNumber)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSendSms = async () => {
    if (smsLoading || !displayOrder.id) return
    setSmsLoading(true)
    setSmsStatus('idle')
    try {
      await ordersApi.sendDriverSms(displayOrder.id)
      setSmsStatus('success')
      setTimeout(() => setSmsStatus('idle'), 3000)
    } catch {
      setSmsStatus('error')
      setTimeout(() => setSmsStatus('idle'), 4000)
    } finally {
      setSmsLoading(false)
    }
  }

  // Header content for BaseDrawer
  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <Package className="w-6 h-6 text-white" />
      </div>
      <div>
        <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm mb-1">
          <StatusIcon className="w-3 h-3 me-1" />
          {t(`orders.${displayOrder.status}`)}
        </Badge>
        <h2 className="text-lg font-bold text-white">
          {productLabel || t('orders.product')}
        </h2>
      </div>
    </div>
  )

  // Top bar content (Order ID)
  const topBarContent = (
    <>
      <span className="text-sm text-slate-500 dark:text-slate-400">{t('orders.id')}</span>
      <button
        onClick={copyOrderId}
        className="flex items-center gap-2 text-sm font-mono font-semibold text-primary hover:text-primary/80 transition-colors"
      >
        {displayOrder.orderNumber}
        {copied ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
      </button>
    </>
  )

  // Footer content
  const footerContent = (
    <div className="flex flex-wrap gap-3">
      {showLegacyDrawerActions && onDownloadPdf ? (
        <Button
          variant="default"
          className="h-12 rounded-xl gap-2 hover:bg-primary/90"
          disabled={downloadLoading}
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (downloadLoading) return
            try {
              setDownloadLoading(true)
              await onDownloadPdf(displayOrder)
            } finally {
              setDownloadLoading(false)
            }
          }}
        >
          {downloadLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {t('orders.downloadQrPDF') ?? t('orders.downloadPDF')}
        </Button>
      ) : null}

      {showLegacyDrawerActions && !isPackhouseManager ? (
        <Button
          variant="outline"
          className={cn(
            'h-12 rounded-xl gap-2 bg-white dark:bg-slate-800',
            smsStatus === 'success' && 'border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
            smsStatus === 'error' && 'border-red-200 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
            smsStatus === 'idle' && 'border-sky-200 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20',
          )}
          disabled={smsLoading}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleSendSms()
          }}
        >
          {smsLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : smsStatus === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : smsStatus === 'error' ? (
            <XCircle className="w-4 h-4" />
          ) : (
            <MessageSquare className="w-4 h-4" />
          )}
          {smsLoading
            ? (t('orders.sendingSms') ?? 'Sending...')
            : smsStatus === 'success'
              ? (t('orders.smsSent') ?? 'SMS Sent')
              : smsStatus === 'error'
                ? (t('orders.smsError') ?? 'SMS Failed')
                : (t('orders.sendSmsToDriver') ?? 'Send SMS to Driver')}
        </Button>
      ) : null}

      {showDeliveryNoteDownload && onDownloadDeliveryNote ? (
        <Button
          variant="default"
          className="h-12 rounded-xl gap-2 hover:bg-primary/90"
          disabled={downloadLoading}
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (downloadLoading) return
            try {
              setDownloadLoading(true)
              await onDownloadDeliveryNote(displayOrder)
            } finally {
              setDownloadLoading(false)
            }
          }}
        >
          {downloadLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {downloadLoading
            ? (t('orders.downloadingDeliveryNote') ?? 'Downloading...')
            : preferDeliveryNotePreview
              ? (t('orders.previewDeliveryNote') ?? 'Preview delivery note')
              : (t('orders.downloadDeliveryNote') ?? 'Download Delivery Note')}
        </Button>
      ) : null}

      {canCancelOrder && (
        <Button
          variant="outline"
          className="h-12 rounded-xl border-red-200 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 gap-2 bg-white dark:bg-slate-800"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel?.(displayOrder); }}
        >
          <XCircle className="w-4 h-4" />
          {t('orders.cancel')}
        </Button>
      )}
    </div>
  )

  // Tabs content for stickyContent
  const tabsContent = (
    <div className="px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-border/20">
      <div className="glass-card rounded-2xl overflow-hidden border border-border/50 flex">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'overview'
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Layers className={cn("w-4 h-4", activeTab === 'overview' && "text-primary")} />
          {t('orders.overview')}
          {activeTab === 'overview' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('shipments')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'shipments'
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Truck className={cn("w-4 h-4", activeTab === 'shipments' && "text-primary")} />
          {t('orders.shipment')}
          {activeTab === 'shipments' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('weighbridge')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'weighbridge'
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Gauge className={cn("w-4 h-4", activeTab === 'weighbridge' && "text-primary")} />
          {t('orders.weighbridge')}
          {activeTab === 'weighbridge' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'timeline'
              ? "bg-primary/15 text-primary font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Activity className={cn("w-4 h-4", activeTab === 'timeline' && "text-primary")} />
          {t('orders.timeline')}
          {activeTab === 'timeline' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        {userRoleType === 'super_admin' && (
          <button
            onClick={() => setActiveTab('sap-sync')}
            className={cn(
              "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
              activeTab === 'sap-sync'
                ? "bg-primary/15 text-primary font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            )}
          >
            <RefreshCw className={cn("w-4 h-4", activeTab === 'sap-sync' && "text-primary")} />
            {t('orders.sapSync')}
            {activeTab === 'sap-sync' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
            )}
          </button>
        )}
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={config.gradient}
      headerContent={headerContent}
      topBarContent={topBarContent}
      stickyContent={tabsContent}
      footerContent={footerContent}
      fixedWidth={600}
      title={t('orders.orderDetails')}
    >
      {/* Tab Content - scrollable */}
      {activeTab === 'overview' && (
              <>
                {/* Progress Tracker */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center justify-between">
                    {statusSteps.map((step, index) => {
                      const stepConfig = statusConfig[step]
                      const StepIcon = stepConfig.icon
                      const isCompleted = index <= currentStepIndex
                      const isCurrent = index === currentStepIndex

                      return (
                        <div key={step} className="flex flex-col items-center flex-1 relative">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-[background-color,border-color] z-10 ${
                              isCompleted ? `bg-gradient-to-br ${stepConfig.gradient} shadow-lg` : 'bg-slate-200 dark:bg-slate-600'
                            } ${isCurrent ? 'ring-4 ring-primary/20 scale-110' : ''}`}
                          >
                            <StepIcon className={`w-5 h-5 ${isCompleted ? 'text-white' : 'text-slate-400'}`} />
                          </div>
                          {index < statusSteps.length - 1 && (
                            <div
                              className={`absolute top-5 start-[55%] w-[calc(100%-10px)] h-0.5 ${
                                index < currentStepIndex ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-600'
                              }`}
                            />
                          )}
                          <p
                            className={`text-[10px] mt-2 font-medium text-center ${
                              isCompleted ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'
                            }`}
                          >
                            {t(`orders.${step}`)}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Sub-Customer (displayed as "Customer" in portal) */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">{t('orders.subCustomer')}</p>
                    </div>
                    {!isEditingSubCustomer && !isPackhouseManager && (
                      <button
                        type="button"
                        onClick={() => setIsEditingSubCustomer(true)}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                        {displayOrder.subCustomerName
                          ? t('orders.changeSubCustomer')
                          : t('orders.selectSubCustomer')}
                      </button>
                    )}
                  </div>

                  {isEditingSubCustomer ? (
                    <SubCustomerSelector
                      currentSubCustomer={
                        displayOrder.subCustomerId
                          ? {
                              id: displayOrder.subCustomerId,
                              documentId: displayOrder.subCustomerDocumentId ?? displayOrder.subCustomerId,
                              name: displayOrder.subCustomerName ?? '',
                              phone: displayOrder.subCustomerPhone ?? null,
                              code: displayOrder.subCustomerCode ?? '',
                              createdAt: '',
                              updatedAt: '',
                            }
                          : null
                      }
                      onSelect={(sc) => handleSubCustomerSelected(sc)}
                      onClear={() => handleSubCustomerSelected(null)}
                      onCancel={() => setIsEditingSubCustomer(false)}
                      loading={subCustomerSaving}
                      tenantDocumentId={displayOrder.tenantDocumentId}
                    />
                  ) : displayOrder.subCustomerName ? (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{displayOrder.subCustomerName}</p>
                        {displayOrder.subCustomerCode && (
                          <p className="text-xs text-muted-foreground font-mono">{displayOrder.subCustomerCode}</p>
                        )}
                        {displayOrder.subCustomerPhone && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground">{displayOrder.subCustomerPhone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('orders.noSubCustomer')}</p>
                  )}
                </div>

                {/* SAP Sales Order Number */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">{t('orders.sapSalesOrderNumber')}</p>
                  </div>
                  <p className="text-xl font-bold text-foreground font-mono">
                    {displayOrder.sapSalesOrderNumber?.trim() || t('common.notAvailable')}
                  </p>
                </div>

                {/* Invoice Info */}
                {orderInvoice && (
                  <div className="glass-card p-5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Receipt className="w-4 h-4 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">{t('finance.invoice', 'Invoice')}</p>
                    </div>
                    <p className="text-sm font-semibold text-foreground">{orderInvoice.invoiceNumber}</p>
                    <p className="text-lg font-bold text-foreground mt-1">
                      {orderInvoice.currency} {orderInvoice.total.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Quantity */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">{t('orders.quantity')}</p>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {product?.quantity.value} {quantityUnit}
                  </p>
                </div>

                {/* Dates */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">{t('orders.schedule')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('orders.orderDate')}</p>
                      <p className="text-sm font-medium text-foreground">{displayOrder.orderDate}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('orders.deliveryDate')}</p>
                      <p className="text-sm font-medium text-foreground">{displayOrder.deliveryDate}</p>
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground mb-1">
                        {t('orders.deliveryAddress')}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {deliveryLocation}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Related Sales Order */}
                {displayOrder.contractId && (
                  <div
                    className={cn(
                      "flex items-center justify-between p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 transition-colors group",
                      "hover:bg-indigo-100 dark:hover:bg-indigo-900/30 cursor-pointer"
                    )}
                    onClick={handleViewSalesOrderClick}
                    role="button"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('orders.salesOrder')}</p>
                        <p className="text-xs text-muted-foreground">{t('orders.viewSalesOrder')}</p>
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-indigo-500" />
                  </div>
                )}

                {/* Sales Order Created By — visible to super admins only */}
                {isSuperAdmin && displayOrder.salesOrderCreatedByUser && (
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">{t('orders.salesOrderCreatedBy')}</p>
                    <p className="text-sm text-foreground">
                      {displayOrder.salesOrderCreatedByUser.fullName ||
                        displayOrder.salesOrderCreatedByUser.username ||
                        displayOrder.salesOrderCreatedByUser.email ||
                        String(displayOrder.salesOrderCreatedByUser.id)}
                    </p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'shipments' && (
              <>
                {showLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : shipments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <Truck className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{t('shipments.noShipments')}</p>
                    <p className="text-xs text-muted-foreground">{t('shipments.noShipmentsForOrder')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shipments.map((shipment) => (
                      <ShipmentCard
                        key={shipment.id}
                        shipment={shipment}
                        onClick={onViewShipment ? () => onViewShipment(shipment.id) : undefined}
                        onResourceUpdated={handleShipmentResourceUpdated}
                        tenantDocumentId={displayOrder.tenantDocumentId}
                        readOnly={isPackhouseManager || !isOrderEditable}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'weighbridge' && (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="glass-card p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-4">
                    <Gauge className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">{t('orders.weighbridge')}</p>
                  </div>
                  
                  {showLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-x-4 gap-y-6">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('orders.weighbridgeGross')}</p>
                        <p className="text-sm font-bold text-foreground">
                          {formatWeightValue(summary.grossKg, summary.hasGross)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('orders.weighbridgeTare')}</p>
                        <p className="text-sm font-bold text-foreground">
                          {formatWeightValue(summary.tareKg, summary.hasTare)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('orders.weighbridgeNet')}</p>
                        <p className="text-sm font-bold text-primary">
                          {formatWeightValue(summary.netKg, summary.hasNet)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Weighbridge Timeline */}
                <div className="space-y-4 mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <h3 className="text-sm font-semibold px-1">
                    {t('orders.weighbridgeTimeline')}
                  </h3>
                  {order && (
                    <OrderTimelineTab
                      orderId={order.id}
                      entityTypes={['WEIGHBRIDGE_TICKET', 'WEIGHT_CORRECTION']}
                    />
                  )}
                </div>
              </div>
            )}

            {activeTab === 'timeline' && order && (
              <OrderTimelineTab orderId={order.id} />
            )}

            {activeTab === 'sap-sync' && userRoleType == "super_admin" && order && (
              <OrderSapSyncTab order={order} />
            )}

    </BaseDrawer>
  )
}
